// export default IncidentRequest;
import Loading from "components/Loading";
import { useAuth } from "context/AuthContext";
import { useTooltip } from "context/TooltipProvider";
import React, { useEffect, useState } from "react";
import { FaBriefcase } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import {
  fetchCivilCaseComplaintByAdminAndOrg,
  fetchCivilCaseComplaintForRegistrar,
} from "services/civilCaseComplaintService";
import { fetchComplaintByClientId } from "services/complaintService";
import {
  CivilCaseComplaint,
  CivilCaseComplaintStatus,
  CivilCaseResolutionDecisionType,
  ComplaintStatus,
  Roles,
} from "types";
import { DateView, hasRole } from "utils/helper";
import PageHeader from "../../components/PageHeader";
import { ITEMS_PER_PAGE } from "utils/constant";
import { Gavel, Monitor, Smartphone } from "lucide-react";

// Tabs for display
const tab_for_all = [
  { key: "All", label: "All" },
  { key: "pending", label: ComplaintStatus.pending },
  { key: "on_appointment", label: ComplaintStatus.on_appointment },
  { key: "resolved", label: ComplaintStatus.resolved },
  { key: "appealed", label: ComplaintStatus.appealed },
  { key: "mobile_request", label: "Mobile Request" },
];
const tab_for_atorny = [
  { key: "All", label: "All" },
  { key: "pending", label: ComplaintStatus.pending },
  { key: "on_appointment", label: ComplaintStatus.on_appointment },
  { key: "resolved", label: ComplaintStatus.resolved },
  { key: "appealed", label: ComplaintStatus.appealed },
  { key: "mobile_request", label: "Mobile Request" },
  { key: "search_by_client", label: "Search By Application Number" },
];
const tab_for_registrar = [
  { key: "All", label: "All" },
  { key: "pending", label: ComplaintStatus.pending },
  { key: "registered", label: "Registered" },
];

const CivilCaseComplaintList: React.FC = () => {
  const { showTooltip } = useTooltip();
  const { loginData } = useAuth();
  const [activeTab, setActiveTab] = useState("All");
  const [compliants, setCompliants] = useState<CivilCaseComplaint[]>([]);
  const [compliantsByClientId, setCompliantsByClientId] = useState<
    CivilCaseComplaint[]
  >([]);
  const [loading, setLoading] = useState(false);
  const [tabs, setTabs] = useState(tab_for_all);

  const [clientId, setClientId] = useState("");
  const [search, setSearch] = useState("");
  const navigate = useNavigate();
  const isClerk = hasRole(loginData?.roles, [Roles.complaintClerk]);
  const isRegistrar = hasRole(loginData?.roles, [Roles.complaintRegistrar]);
  const isProsecutor = hasRole(loginData?.roles, [Roles.complaintProsecutor]);

  const fetchData = async () => {
    setLoading(true);
    const api = isRegistrar
      ? fetchCivilCaseComplaintForRegistrar(loginData?.admin_unit + "")
      : fetchCivilCaseComplaintByAdminAndOrg(
          loginData?.admin_unit + "",
          loginData?.org_unit + "",
        );
    const [complaintRes] = await Promise.all([api]);

    if (complaintRes) {
      const filteredData = isRegistrar
        ? complaintRes.filter((complaint) =>
            ["forwarded_to_registrar", "resolved"].includes(
              complaint.status ?? "",
            ),
          )
        : isProsecutor
          ? complaintRes?.filter(
              (a) => a?.user_id_assgined_prosecutor === loginData?.user_id,
            )
          : complaintRes;
      setCompliants(
        filteredData?.sort(
          (a: any, b: any) =>
            new Date(b.created_at).getTime() - new Date(a.created_at).getTime(),
        ) || [],
      );
      setLoading(false);
    }
  };

  const fetchByClientId = async () => {
    if (!clientId) return;
    setLoading(true);
    try {
      const [complaintRes] = await Promise.all([
        fetchComplaintByClientId(clientId),
      ]);
      // console.log(complaintRes);
      // setCompliantsByClientId([]);
      setCompliantsByClientId(
        Array.isArray(complaintRes) ? [...(complaintRes as any)] : [],
      );
    } catch (error: any) {
      setCompliantsByClientId([]);
      showTooltip(
        error?.response?.data?.message ||
          "No complaints found for this client ID.",
        "error",
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (loginData) {
      fetchData();

      if (
        loginData?.structure?.admin_unit &&
        loginData?.structure?.org_unit &&
        !loginData?.structure?.admin_unit?.parent_unit &&
        !loginData?.structure?.org_unit?.parent_unit
      ) {
        setTabs(tab_for_atorny);
      } else if (isRegistrar) {
        setTabs(tab_for_registrar);
      }
    }
  }, [activeTab, loginData]);

  const searchTerm = search.trim().toLowerCase();
  const filtered = compliants.filter((complaint) => {
    // 1️⃣ — filter by status tab
    if (activeTab !== "All") {
      const tabKey = tabs.find((tab) => tab.label === activeTab)?.key;

      // Special handling for pending tab based on user role
      if (tabKey === "pending") {
        // For registrar role - show only forwarded_to_registrar status
        if (isRegistrar) {
          if (!["forwarded_to_registrar"].includes(complaint.status ?? "")) {
            return false;
          }
        }
        // For non-registrar roles - show pending and forwarded_to_management
        else {
          if (
            ![
              "pending",
              "forwarded_to_cordinator",
              "forwarded_to_registrar",
            ].includes(complaint.status ?? "")
          ) {
            return false;
          }
        }
      }
      // Handle registered tab - filter by presence of registration_number
      else if (tabKey === "registered") {
        // Show only complaints that have a registration number
        if (!["resolved"].includes(complaint.status ?? "")) {
          return false;
        }
      } else if (tabKey === "appealed") {
        // Show only complaints that have a registration number
        if (!complaint?.is_appeal) {
          return false;
        }
      }
      // Handle mobile_request tab - filter by is_complaint_not_by_mobile flag
      else if (tabKey === "mobile_request") {
        // Show complaints that are NOT by mobile (is_complaint_not_by_mobile = true)
        if (complaint.is_complaint_not_by_mobile) {
          return false;
        }
      }
      // For other tabs (except "All" and "search_by_client"), check if status matches exactly
      else if (tabKey !== "search_by_client" && complaint.status !== tabKey) {
        return false;
      }
      // For "search_by_client" tab, we don't filter by status
    }

    // 2️⃣ — filter by search text
    const client_id = complaint?.client_id?.toString()?.toLowerCase() || "";
    const complaint_type =
      complaint?.complaint_type?.toString()?.toLowerCase() || "";
    const phone_number =
      complaint?.phone_number?.toString()?.toLowerCase() || "";
    const application_number =
      complaint?.application_number?.toString()?.toLowerCase() || "";

    const matchesText =
      client_id.includes(searchTerm) ||
      complaint_type.includes(searchTerm) ||
      phone_number.includes(searchTerm) ||
      application_number.includes(searchTerm);

    return matchesText;
  });

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);

  // Calculate paginated incidents
  const indexOfLastItem = currentPage * ITEMS_PER_PAGE;
  const indexOfFirstItem = indexOfLastItem - ITEMS_PER_PAGE;
  const paginatedData = filtered.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filtered.length / ITEMS_PER_PAGE);

  return (
    <div className="max-w-screen-xl ml-1 mr-4 space-y-6">
      <PageHeader
        title="Complaint on Prosecutor Request"
        icon={FaBriefcase}
        tabs={tabs?.map((t) => t?.label)}
        activeTab={activeTab}
        onTabClick={setActiveTab}
        rightContent={
          <>
            {isClerk && (
              <button
                onClick={() => navigate("register")}
                className="px-5 py-2 text-sm mt-8 font-semibold text-white bg-red-600 rounded hover:bg-red-700"
              >
                Create a new Compliant
              </button>
            )}
          </>
        }
      />

      {activeTab === "Search By Application Number" && (
        <div className="max-w-screen-xl ml-1 mr-4 space-y-6">
          <div className="overflow-hidden bg-white shadow rounded-xl">
            {/* add text field for client and and button called search when click show complaint info */}
            <div className="p-4 flex flex-row items-center gap-5">
              <input
                type="text"
                placeholder="Enter Client ID"
                className="border border-gray-300 rounded-md p-2 w-full"
                value={clientId}
                onChange={(e) => setClientId(e.target.value)}
              />
              <button
                className="px-4 py-2 bg-black text-white rounded-md"
                onClick={fetchByClientId}
              >
                Search
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="max-w-screen-xl ml-1 mr-4 space-y-6">
        <div className="overflow-hidden bg-white shadow rounded-xl">
          {loading ? (
            <Loading label="Loading Compliant" />
          ) : (
            <table className="w-full text-sm border-collapse">
              <thead className="text-white bg-black">
                <tr>
                  <th colSpan={7} className="p-4" rounded-t-lg>
                    <div className="flex items-center justify-between">
                      {/* Left: Back + Title */}
                      <div className="flex items-center gap-2 text-lg font-semibold">
                        <button
                          onClick={() => navigate(-1)}
                          className="text-white hover:text-gray-300"
                          title="Go Back"
                        >
                          ◀️
                        </button>
                        <span>Request Table</span>
                      </div>

                      {/* Right: Search Box */}
                      <div className="relative w-80">
                        <span className="absolute inset-y-0 flex items-center text-gray-500 pointer-events-none left-3">
                          🔍
                        </span>
                        <input
                          type="text"
                          value={search}
                          onChange={(e) => setSearch(e.target.value)}
                          placeholder="Search"
                          className="w-full py-2 pl-10 pr-4 text-sm text-black placeholder-gray-600 bg-gray-200 rounded-md focus:outline-none"
                        />
                      </div>
                    </div>
                  </th>
                </tr>
                <tr>
                  <th className="p-3 text-left">Compliant Req</th>
                  <th className="p-3 text-left">Reception Date</th>
                  <th className="p-3 text-left">Phone Number</th>
                  <th className="p-3 text-left">Status</th>
                  <th className="p-3 text-left">Application Number</th>
                  <th className="p-3 text-left" rounded-tr-lg>
                    View Action
                  </th>
                </tr>
              </thead>
              <tbody>
                {(activeTab === "Search By Application Number"
                  ? compliantsByClientId
                  : paginatedData
                ).map((data, idx) => {
                  // find latest resolution by updated_at and get its status
                  const latestResolution = data.resolutions?.sort(
                    (a: any, b: any) =>
                      new Date(a?.updated_at) < new Date(b?.updated_at ?? 0)
                        ? 1
                        : -1,
                  );
                  const decision_type =
                    latestResolution && latestResolution[0]?.decision_type;
                  return (
                    <tr
                      key={idx}
                      className={idx % 2 === 0 ? "bg-[#F7F4F1AB]" : "bg-white"}
                    >
                      <td className="p-3 font-[500]  flex flex-row items-center gap-2">
                        <img
                          src="/complient.png"
                          alt="complient"
                          className="w-10 h-10"
                        />
                        {data?.client_id ?? "N/A"}
                      </td>

                      <td className="p-3 font-[500] ">
                        {DateView(data.complaint_reception_date) || "N/A"}
                      </td>

                      <td className="p-3 font-[500] ">
                        {data?.phone_number || "N/A"}
                      </td>
                      <td className="p-3 font-[500] ">
                        <span className="text-2xl font-semibold text-gray-700 flex items-center gap-2">
                          {data?.status && (
                            <span
                              className={`px-4 py-1 rounded-full text-sm font-bold ${
                                data.status === "resolved"
                                  ? "bg-green-100 text-green-700 border border-green-300"
                                  : data.status === "pending"
                                    ? "bg-yellow-100 text-yellow-800 border border-yellow-300"
                                    : data.status === "closed"
                                      ? "bg-gray-200 text-gray-700 border border-gray-300"
                                      : data.status ===
                                          "forwarded_to_cordinator"
                                        ? "bg-red-100 text-red-700 border border-red-300"
                                        : data.status === "under_review"
                                          ? "bg-purple-100 text-purple-700 border border-purple-300"
                                          : data.status === "appealed"
                                            ? "bg-red-100 text-red-700 border border-red-300"
                                            : data.status ===
                                                "assigned_to_prosecutor"
                                              ? "bg-indigo-100 text-red-700 border border-indigo-300"
                                              : data.status === "on_appointment"
                                                ? "bg-orange-100 text-orange-700 border border-orange-300"
                                                : "bg-gray-100 text-gray-700 border border-gray-300"
                              }
                             `}
                            >
                              {decision_type ===
                                "escalate_to_higher_authority" ||
                              decision_type === "forward_to_lower_authority"
                                ? CivilCaseResolutionDecisionType[decision_type]
                                : CivilCaseComplaintStatus[data.status]}
                            </span>
                          )}
                        </span>{" "}
                      </td>
                      <td className="p-3 font-[500] ">
                        {data?.application_number || "N/A"}
                      </td>
                      <td className="p-3 font-[500]">
                        <div className="flex flex-row items-center align-middle gap-2">
                          <button
                            type="button"
                            onClick={() => {
                              navigate(data?.id + "/");
                            }}
                            className="px-4 py-1 text-sm bg-gray-200 rounded hover:bg-gray-300"
                          >
                            View
                          </button>
                          <div className="font-[500]">
                            {data?.is_appeal ? (
                              <Gavel className="w-5 h-5 text-red-600" />
                            ) : (
                              <Gavel className="w-5 h-5 text-gray-300" />
                            )}
                          </div>
                          <div className="font-[500]">
                            {data?.is_complaint_not_by_mobile ? (
                              <Monitor className="w-5 h-5 text-blue-600" />
                            ) : (
                              <Smartphone className="w-5 h-5 text-green-600" />
                            )}
                          </div>
                        </div>
                      </td>
                    </tr>
                  );
                })}
                {(activeTab === "Search By Application Number"
                  ? compliantsByClientId
                  : filtered
                ).length === 0 && (
                  <tr>
                    <td colSpan={6} className="p-4 text-center text-gray-500">
                      No compliant found.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          )}
        </div>
        {totalPages > 1 && (
          <div className="flex justify-center items-center gap-2 mt-4">
            <button
              onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
              disabled={currentPage === 1}
              className="px-3 py-1 rounded bg-gray-200 hover:bg-gray-300 disabled:opacity-50"
            >
              Prev
            </button>
            <span className="px-2">
              Page {currentPage} of {totalPages}
            </span>
            <button
              onClick={() =>
                setCurrentPage((prev) => Math.min(prev + 1, totalPages))
              }
              disabled={currentPage === totalPages}
              className="px-3 py-1 rounded bg-gray-200 hover:bg-gray-300 disabled:opacity-50"
            >
              Next
            </button>
          </div>
        )}
      </div>

      {/* {isOpenFurtherInvestigation && selectedFile && (
        <FurtherInvestigationForm
          isOpen={isOpenFurtherInvestigation}
          action={"Edit"}
          file={undefined}
          furtherInvestigation={selectedFile}
          onClose={(refetch) => {
            setIsOpenFurtherInvestigation(false);

            if (refetch) {
              fetchData();
            }
          }}
        />
      )} */}
    </div>
  );
};

export default CivilCaseComplaintList;
